import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Typography from '../../Typography';
import CreditLotSummary from './CreditLotSummary';
import UsageLedger from './UsageLedger';
import {
  buildUsageBannerMessage,
  clampPct,
  effectiveLotPct,
  isLotExhausted,
  resolveUsageBand,
} from '../../../utils/constants/creditUsageThresholds';
import { useCreditsSummary } from '../../../hooks/billing';

/**
 * ROAR-4943 — the Credits & Usage pane (AC1, AC2, AC3).
 *
 * AC1 renders an ARRAY of credit lots — every active lot regardless of `source`: `subscription`
 * (the self-serve cycle), `manual` (the enterprise per-period grant) and `beta` (promotional). Each
 * gets its own block and its own band colour (ROAR-4899 §4 decision 7).
 *
 * The banner, by contrast, renders ONCE for the whole pane — several stacked warnings would not fit
 * the modal. See `worstLotBanner` for the exact rule.
 *
 * ROAR-4962 added a separate **This Cycle** summary above the list, fed by the contract's
 * top-level `cycle` object. ROAR-4899 removed both: the subscription lot is already in `lots[]`,
 * labelled 'This Cycle' by the backend's source registry and carrying the same
 * `used`/`allotment`/`pct`/`resets_at`, so the pane was rendering one lot twice. `requirement.md`'s
 * `:145` this-cycle summary is satisfied by that lot's own block; `:148`'s balance widget and
 * expiring-soon breakdown by the list and the balance line below it.
 */

/**
 * Contract order, re-applied client-side: `resets_at` ASC then `id` ASC.
 *
 * The backend sorts too, but the source query it is built on carries no ORDER BY (§4 decision 13),
 * so without this the blocks could reshuffle between refetches and a user would watch them jump.
 */
const byResetThenId = (a, b) => {
  // A missing OR unparseable `resets_at` sorts last rather than producing NaN — a NaN comparison
  // makes Array.prototype.sort's result implementation-defined, which is exactly the instability
  // this comparator exists to prevent.
  const timestamp = (lot) => {
    const parsed = lot?.resets_at ? new Date(lot.resets_at).getTime() : NaN;
    return Number.isFinite(parsed) ? parsed : Infinity;
  };
  const aReset = timestamp(a);
  const bReset = timestamp(b);
  if (aReset !== bReset) return aReset - bReset;

  // Numeric ids compare numerically; anything else falls back to a plain string compare. Sorting
  // numeric ids as strings would put lot 10 before lot 9 and diverge from the contract's `id ASC`.
  const aId = a?.id;
  const bId = b?.id;
  if (typeof aId === 'number' && typeof bId === 'number') return aId - bId;
  return String(aId) < String(bId) ? -1 : String(aId) > String(bId) ? 1 : 0;
};

/**
 * The single pane-level banner, per §4 decision 7.
 *
 * - An empty `lots` array yields NO banner. This short-circuit is the point: with no lots there is
 *   no lot to name, and `Math.max()` over an empty array would yield `-Infinity`.
 * - Otherwise the worst band across lots wins. The `default` band has no banner, so a pane where
 *   everything sits under 50% shows nothing.
 * - Among the lots whose own band is the worst one, the banner cites the **earliest-resetting**
 *   lot — the more urgent of two equally-full lots — falling back to render order on a tie.
 * - The copy NAMES that lot, so a `beta` or `manual` grant can never be mislabelled as the cycle.
 *
 * @param {Array<object>} lots Credit lots, already in render order.
 * @returns {{band: object, message: string}|null}
 */
const worstLotBanner = (lots) => {
  if (!lots.length) return null;

  const worstBand = resolveUsageBand(
    Math.max(...lots.map((lot) => clampPct(effectiveLotPct(lot))))
  );
  if (!worstBand?.banner) return null;

  const tied = lots.filter((lot) => resolveUsageBand(effectiveLotPct(lot)).tone === worstBand.tone);
  // An exhausted lot outranks a merely-critical one: "you've reached your credit limit" is the more
  // urgent message, and an earlier-resetting 85% lot must not hide it.
  const exhausted = tied.filter(isLotExhausted);
  const cited = [...(exhausted.length ? exhausted : tied)].sort(byResetThenId)[0];
  const resetDate = cited?.resets_at ? moment(cited.resets_at).format('MMM D') : 'soon';

  return { band: worstBand, message: buildUsageBannerMessage(worstBand, cited, resetDate) };
};

const CreditsUsage = ({ onNavigate }) => {
  const { data, isLoading, isError } = useCreditsSummary();

  const lots = useMemo(() => [...(data?.lots || [])].sort(byResetThenId), [data]);
  const banner = useMemo(() => worstLotBanner(lots), [lots]);

  const balance = data?.balance;

  return (
    <div>
      <Typography className={'text-headings text-sm font-semibold'}>Account Credits</Typography>

      {/* Only when there is nothing to show. A failed REFETCH still holds the previous lots in
          cache, and an error line stacked above a full, readable list would be worse than either
          state on its own. */}
      {isError && !lots.length && (
        <Typography variant={'body2'} className={'mt-2 text-body'}>
          Couldn&apos;t load your credits. Try again.
        </Typography>
      )}

      {!isLoading && !isError && !lots.length && (
        <Typography variant={'body2'} className={'mt-2 text-body'}>
          No credits allocated yet
        </Typography>
      )}

      {/* A long list scrolls inside the pane rather than growing the modal; the heading and the
          banner deliberately stay outside the scroll region. */}
      {!!lots.length && (
        <div className={'mt-2 max-h-[280px] overflow-y-auto'}>
          {lots.map((lot) => (
            <div key={lot.id} data-testid={'credit-lot'} data-lot-id={lot.id}>
              <CreditLotSummary lot={lot} />
            </div>
          ))}
        </div>
      )}

      {banner && (
        <div
          data-testid={'credit-usage-banner'}
          className={`mt-3 flex items-center gap-2 rounded-md border px-3 py-2 max-content-width ${banner.band.banner.className}`}
        >
          {/* The icon is the only band-coloured element; the copy stays heading-coloured in both
              bands so the red banner escalates through chrome, not through shouting text. */}
          <ErrorOutlineIcon fontSize={'small'} className={banner.band.banner.iconClassName} />
          <Typography variant={'body2'}>{banner.message}</Typography>
        </div>
      )}

      {/* `!= null` rather than a truthiness check: a real balance of 0 must still render, but a
          null one must not become the literal "null credits available".

          `reserved` is appended only when there IS a hold. It is a different quantity from
          `available` — a reservation is credits already spoken for but not yet consumed
          (requirement.md:21) — so a user who cannot reconcile the two needs to see it; but a
          permanent "· 0 reserved" on the common case is noise. */}
      {balance?.available != null && (
        <Typography variant={'body2'} className={'mt-3 text-body'}>
          {[
            `${balance.available} credits available`,
            // `Number(...) > 0` rather than truthiness. `credit_balances.reserved` is a non-null
            // INTEGER today, so plain truthiness would also work — this guards the shape, not a
            // known bug: sibling numeric fields on this pane DO arrive as strings (Postgres
            // NUMERIC/BIGINT serialize that way through Sequelize, see `clampPct`), and the string
            // "0" is truthy, which would pin a permanent "· 0 reserved" onto the common case.
            Number(balance.reserved) > 0 ? `${balance.reserved} reserved` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      )}

      <div className={'mt-4 border-t border-[#0000001A] pt-2'}>
        <UsageLedger onNavigate={onNavigate} />
      </div>
    </div>
  );
};

CreditsUsage.propTypes = {
  /** The Settings shell's close-then-navigate wrapper (§4 decision 2). */
  onNavigate: PropTypes.func,
};

export default CreditsUsage;
